import { IntendedDisplay } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { BannerCarouselSlide } from '@iheartjane/menu';
import { BannerCarousel, useIsModalOpen } from '@iheartjane/menu';
import { Box, Skeleton, useMobileMediaQuery } from '@iheartjane/reefer';
import { useLocationOrigin } from '@iheartjane/utils';
import { useCallback, useMemo } from 'react';

import { useSearchContext } from '@jane/search/hooks';
import { useMenu, useMenuAds } from '@jane/shared-ecomm/providers';

import {
  buildCustomImageAdSlide,
  getMobileCarouselSlides,
  storeHasVisibleCarousel,
  toBannerCarouselSlides,
} from './MenuBannerCarousel.util';

export const MenuBannerCarousel = () => {
  const origin = useLocationOrigin();
  const { paths, store } = useMenu();
  const isMobile = useMobileMediaQuery({});
  const placeSecond = useFlag(FLAGS.p1PlaceImageAdSecond, false);
  const { searchState, setSearchState } = useSearchContext();

  const isModalOpen = useIsModalOpen();

  const ads = useMenuAds();
  const instance = ads?.imageAd;
  const isLoading = ads?.isLoading ?? false;

  const adSlide = useMemo(
    () =>
      buildCustomImageAdSlide({
        instance,
        intendedDisplay: isMobile
          ? IntendedDisplay.Mobile
          : IntendedDisplay.Desktop,
        onImageClick: (brandName) => {
          if (!brandName) return;

          setSearchState({
            filters: { ...searchState.filters, brand: [brandName] },
          });
        },
        toPdpUrl: ({ brand, product_id }) =>
          paths.menuProduct({ brand, id: product_id }),
      }),
    [instance, isMobile, paths, searchState, setSearchState]
  );

  const slides = useMemo<BannerCarouselSlide[]>(() => {
    const desktopCarouselSlides =
      store?.carousel_banner?.filter((i) => i.breakpoint === 'desktop') || [];

    const mobileCarouselSlides =
      store?.carousel_banner?.filter((i) => i.breakpoint === 'mobile') || [];

    const storeSlides = storeHasVisibleCarousel(store)
      ? toBannerCarouselSlides(
          isMobile
            ? getMobileCarouselSlides(
                mobileCarouselSlides,
                desktopCarouselSlides
              )
            : desktopCarouselSlides,
          store?.name
        )
      : [];

    if (adSlide == null) return storeSlides;
    if (storeSlides.length === 0) return [adSlide];
    if (!placeSecond) return [adSlide, ...storeSlides];

    return [storeSlides[0], adSlide, ...storeSlides.slice(1)];
  }, [adSlide, isMobile, placeSecond, store]);

  const handleView = useCallback(
    (slide: BannerCarouselSlide) => {
      if (slide?.kind !== 'customImageAd') return;

      instance?.products.forEach((product) => void product.impress());
    },
    [instance]
  );

  // show skeleton when loading to avoid jumps during filter & category changes
  if (isLoading) {
    return (
      <Box px={isMobile ? 24 : 64}>
        <Skeleton animate>
          <Skeleton.Bone
            data-testid="banner-carousel-skeleton"
            height="auto"
            style={{ aspectRatio: isMobile ? '160 / 39' : '149 / 36' }}
          />
        </Skeleton>
      </Box>
    );
  }

  if (!slides.length) return null;

  return (
    <Box px={isMobile ? 24 : 64}>
      <BannerCarousel
        key={isMobile ? 'mobile' : 'desktop'}
        disableAutoAdvance={isModalOpen}
        onView={handleView}
        slides={slides}
        comparisonUrl={origin}
      />
    </Box>
  );
};
